import { useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function AttendanceTracker({ user }) {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [todayLogged, setTodayLogged] = useState(false);
  const [stats, setStats] = useState({ totalDays: 0, totalHours: 0, requiredHours: 900 });
  const [showLogModal, setShowLogModal] = useState(false);
  const [logData, setLogData] = useState({
    date: new Date().toISOString().split('T')[0],
    hours: 6,
    subjects: [],
    notes: ""
  });

  const subjects = [
    "Bible/Scripture Study",
    "Mathematics",
    "Language Arts",
    "Science",
    "History",
    "Hebrew Studies",
    "Physical Education",
    "Art/Music",
    "Foreign Language",
    "Life Skills"
  ];

  useEffect(() => {
    fetchAttendance();
  }, [selectedMonth, selectedYear]);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/api/attendance?month=${selectedMonth + 1}&year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAttendance(response.data.records || []);
      setStats(response.data.stats || { totalDays: 0, totalHours: 0, requiredHours: 900 });
      
      // Check if today is logged
      const today = new Date().toISOString().split('T')[0];
      const todayRecord = response.data.records?.find(r => r.date === today);
      setTodayLogged(!!todayRecord);
    } catch (err) {
      console.error("Error fetching attendance:", err);
    } finally {
      setLoading(false);
    }
  };

  const logAttendance = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/api/attendance`, logData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowLogModal(false);
      fetchAttendance();
      setLogData({
        date: new Date().toISOString().split('T')[0],
        hours: 6,
        subjects: [],
        notes: ""
      });
    } catch (err) {
      console.error("Error logging attendance:", err);
      alert("Failed to log attendance");
    }
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const isSchoolDay = (date) => {
    return attendance.some(r => r.date === date);
  };

  const getAttendanceForDate = (date) => {
    return attendance.find(r => r.date === date);
  };

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
    const days = [];
    
    // Empty cells for days before the first of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const record = getAttendanceForDate(dateStr);
      const isToday = dateStr === new Date().toISOString().split('T')[0];
      
      days.push(
        <div
          key={day}
          className={`calendar-day ${record ? 'attended' : ''} ${isToday ? 'today' : ''}`}
          title={record ? `${record.hours} hours - ${record.subjects?.join(', ')}` : ''}
        >
          <span className="day-number">{day}</span>
          {record && <span className="hours-badge">{record.hours}h</span>}
        </div>
      );
    }
    
    return days;
  };

  const progressPercentage = Math.min((stats.totalHours / stats.requiredHours) * 100, 100);

  return (
    <div className="card" data-testid="attendance-tracker">
      <h3 style={{ color: "#FFD700", marginBottom: "20px" }}>
        Attendance Tracker
      </h3>

      {/* Quick Log Button */}
      {!todayLogged && (
        <div style={{
          background: "linear-gradient(135deg, #1a4d1a 0%, #0a2a0a 100%)",
          border: "2px solid #90EE90",
          borderRadius: "12px",
          padding: "20px",
          marginBottom: "25px",
          textAlign: "center"
        }}>
          <p style={{ color: "#90EE90", marginBottom: "15px", fontSize: "1.1rem" }}>
            Today's attendance not logged yet
          </p>
          <button
            onClick={() => setShowLogModal(true)}
            style={{
              background: "linear-gradient(135deg, #90EE90 0%, #32CD32 100%)",
              color: "#000",
              border: "none",
              padding: "15px 40px",
              fontSize: "1.1rem",
              fontWeight: "bold",
              borderRadius: "8px",
              cursor: "pointer"
            }}
          >
            Log Today's Attendance
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: "15px",
        marginBottom: "25px"
      }}>
        <div style={{
          background: "#1a1a1a",
          padding: "20px",
          borderRadius: "10px",
          textAlign: "center",
          border: "1px solid #333"
        }}>
          <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#FFD700" }}>
            {stats.totalDays}
          </div>
          <div style={{ color: "#888", fontSize: "0.9rem" }}>School Days</div>
        </div>
        <div style={{
          background: "#1a1a1a",
          padding: "20px",
          borderRadius: "10px",
          textAlign: "center",
          border: "1px solid #333"
        }}>
          <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#87CEEB" }}>
            {stats.totalHours}
          </div>
          <div style={{ color: "#888", fontSize: "0.9rem" }}>Total Hours</div>
        </div>
        <div style={{
          background: "#1a1a1a",
          padding: "20px",
          borderRadius: "10px",
          textAlign: "center",
          border: "1px solid #333"
        }}>
          <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#90EE90" }}>
            {stats.requiredHours - stats.totalHours}
          </div>
          <div style={{ color: "#888", fontSize: "0.9rem" }}>Hours Remaining</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div style={{ marginBottom: "30px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
          <span style={{ color: "#aaa" }}>Annual Progress</span>
          <span style={{ color: "#FFD700", fontWeight: "bold" }}>{progressPercentage.toFixed(1)}%</span>
        </div>
        <div style={{
          width: "100%",
          height: "12px",
          background: "#333",
          borderRadius: "6px",
          overflow: "hidden"
        }}>
          <div style={{
            width: `${progressPercentage}%`,
            height: "100%",
            background: progressPercentage >= 100 
              ? "linear-gradient(90deg, #90EE90 0%, #32CD32 100%)"
              : "linear-gradient(90deg, #FFD700 0%, #FFA500 100%)",
            borderRadius: "6px",
            transition: "width 0.5s ease"
          }}></div>
        </div>
        <p style={{ color: "#666", fontSize: "0.85rem", marginTop: "8px" }}>
          {stats.requiredHours} hours required for homeschool compliance (varies by state)
        </p>
      </div>

      {/* Month/Year Selector */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "20px",
        flexWrap: "wrap",
        gap: "10px"
      }}>
        <button
          onClick={() => {
            if (selectedMonth === 0) {
              setSelectedMonth(11);
              setSelectedYear(selectedYear - 1);
            } else {
              setSelectedMonth(selectedMonth - 1);
            }
          }}
          style={{
            background: "#333",
            color: "#eee",
            border: "none",
            padding: "10px 20px",
            borderRadius: "6px",
            cursor: "pointer"
          }}
        >
          Previous
        </button>
        <h4 style={{ color: "#FFD700", margin: 0 }}>
          {months[selectedMonth]} {selectedYear}
        </h4>
        <button
          onClick={() => {
            if (selectedMonth === 11) {
              setSelectedMonth(0);
              setSelectedYear(selectedYear + 1);
            } else {
              setSelectedMonth(selectedMonth + 1);
            }
          }}
          style={{
            background: "#333",
            color: "#eee",
            border: "none",
            padding: "10px 20px",
            borderRadius: "6px",
            cursor: "pointer"
          }}
        >
          Next
        </button>
      </div>

      {/* Calendar */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: "5px",
        marginBottom: "20px"
      }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
          <div key={day} style={{
            textAlign: "center",
            padding: "10px",
            color: "#888",
            fontWeight: "bold",
            fontSize: "0.85rem"
          }}>
            {day}
          </div>
        ))}
        {renderCalendar()}
      </div>

      {/* Legend */}
      <div style={{
        display: "flex",
        gap: "20px",
        justifyContent: "center",
        flexWrap: "wrap",
        padding: "15px",
        background: "#111",
        borderRadius: "8px"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ width: "20px", height: "20px", background: "#90EE90", borderRadius: "4px" }}></div>
          <span style={{ color: "#888" }}>School Day</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ width: "20px", height: "20px", background: "#333", border: "2px solid #FFD700", borderRadius: "4px" }}></div>
          <span style={{ color: "#888" }}>Today</span>
        </div>
      </div>

      {/* Log Modal */}
      {showLogModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.8)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: "20px"
        }} onClick={() => setShowLogModal(false)}>
          <div style={{
            background: "#1a1a1a",
            borderRadius: "15px",
            padding: "30px",
            maxWidth: "500px",
            width: "100%",
            maxHeight: "90vh",
            overflowY: "auto",
            border: "2px solid #FFD700"
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: "#FFD700", marginBottom: "25px" }}>Log Attendance</h3>
            
            <div style={{ marginBottom: "20px" }}>
              <label style={{ color: "#aaa", display: "block", marginBottom: "8px" }}>Date</label>
              <input
                type="date"
                value={logData.date}
                onChange={e => setLogData({ ...logData, date: e.target.value })}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: "#222",
                  border: "2px solid #333",
                  borderRadius: "8px",
                  color: "#eee",
                  fontSize: "1rem"
                }}
              />
            </div>
            
            <div style={{ marginBottom: "20px" }}>
              <label style={{ color: "#aaa", display: "block", marginBottom: "8px" }}>
                Hours Studied: {logData.hours}
              </label>
              <input
                type="range"
                min="1"
                max="12"
                value={logData.hours}
                onChange={e => setLogData({ ...logData, hours: parseInt(e.target.value) })}
                style={{ width: "100%" }}
              />
            </div>
            
            <div style={{ marginBottom: "20px" }}>
              <label style={{ color: "#aaa", display: "block", marginBottom: "12px" }}>Subjects Covered</label>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "8px"
              }}>
                {subjects.map(subject => (
                  <label
                    key={subject}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px",
                      background: logData.subjects.includes(subject) ? "rgba(255,215,0,0.15)" : "#222",
                      border: logData.subjects.includes(subject) ? "1px solid #FFD700" : "1px solid #333",
                      borderRadius: "6px",
                      cursor: "pointer",
                      color: "#ddd",
                      fontSize: "0.9rem"
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={logData.subjects.includes(subject)}
                      onChange={e => {
                        if (e.target.checked) {
                          setLogData({ ...logData, subjects: [...logData.subjects, subject] });
                        } else {
                          setLogData({ ...logData, subjects: logData.subjects.filter(s => s !== subject) });
                        }
                      }}
                    />
                    {subject}
                  </label>
                ))}
              </div>
            </div>
            
            <div style={{ marginBottom: "25px" }}>
              <label style={{ color: "#aaa", display: "block", marginBottom: "8px" }}>Notes (optional)</label>
              <textarea
                value={logData.notes}
                onChange={e => setLogData({ ...logData, notes: e.target.value })}
                placeholder="What was accomplished today..."
                style={{
                  width: "100%",
                  padding: "12px",
                  background: "#222",
                  border: "2px solid #333",
                  borderRadius: "8px",
                  color: "#eee",
                  fontSize: "1rem",
                  minHeight: "80px",
                  resize: "vertical"
                }}
              />
            </div>
            
            <div style={{ display: "flex", gap: "15px" }}>
              <button
                onClick={() => setShowLogModal(false)}
                style={{
                  flex: 1,
                  padding: "15px",
                  background: "#333",
                  color: "#eee",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={logAttendance}
                style={{
                  flex: 1,
                  padding: "15px",
                  background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                  color: "#000",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  cursor: "pointer"
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .calendar-day {
          aspect-ratio: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #1a1a1a;
          border-radius: 8px;
          position: relative;
          border: 1px solid #333;
        }
        
        .calendar-day.empty {
          background: transparent;
          border: none;
        }
        
        .calendar-day.attended {
          background: linear-gradient(135deg, rgba(144,238,144,0.2) 0%, rgba(50,205,50,0.1) 100%);
          border-color: #90EE90;
        }
        
        .calendar-day.today {
          border: 2px solid #FFD700;
        }
        
        .day-number {
          color: #eee;
          font-weight: 500;
        }
        
        .hours-badge {
          position: absolute;
          bottom: 4px;
          font-size: 0.7rem;
          color: #90EE90;
          font-weight: bold;
        }
        
        @media (max-width: 600px) {
          .calendar-day {
            font-size: 0.8rem;
          }
          .hours-badge {
            font-size: 0.6rem;
          }
        }
      `}</style>
    </div>
  );
}

export default AttendanceTracker;
